import { createTheme } from '@mui/material/styles'

// see https://mui.com/material-ui/customization/theming/
// and https://zenoo.github.io/mui-theme-creator/

const sharedTypographyStyles = {
  color: 'rgba(5, 28, 51, 0.87)',
  fontFeatureSettings: "'clig' off, 'liga' off",
  fontStyle: 'normal',
  cursor: 'auto'
}

const themeConfig = createTheme({
  breakpoints: {
    values: {
      phone: 0,
      tablet: 640,
      laptop: 960,
      desktop: 1200,
      wide: 1400
    }
  },
  palette: {
    primary: {
      light: '#647495',
      main: '#3D517B',
      dark: '#2F4572',
      contrast: '#FFFFFF',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#738FCF',
      main: '#5073C3',
      dark: '#385BAB',
      contrast: '#FFFFFF',
      contrastText: '#FFFFFF',
      focus: 'rgba(80, 115, 195, 0.12)',
      focusVisible: 'rgba(80, 115, 195, 0.30)'
    },
    error: {
      opacity: '#D43D4DDD',
      light: '#D43D4D',
      main: '#C33241',
      dark: '#9D1D2A',
      contrast: '#FFFFFF',
      contrastText: '#FFFFFF'
    },
    errorLight: {
      main: '#D43D4D'
    },
    warning: {
      opacity: '#DE6A1ECC',
      light: '#DE6A1E',
      main: '#C35309',
      dark: '#A64200',
      contrast: '#FFFFFF',
      contrastText: '#FFFFFF'
    },
    warningLight: {
      main: '#DE6A1E'
    },
    info: {
      light: '#6281C9',
      main: '#4467B8',
      dark: '#2C4FA0',
      contrast: '#FFFFFF',
      contrastText: '#FFFFFF'
    },
    success: {
      tint: '#E1F7F8',
      lightHover: '#60BEC7',
      light: '#51A9B1',
      main: '#377F86',
      dark: '#22686F',
      contrast: '#FFFFFF',
      contrastText: '#FFFFFF'
    },
    successLight: {
      main: '#60BEC7'
    },
    background: {
      default: '#F5F6F8',
      main: '#F5F6F8',
      mid: '#E7EBF2',
      dark: '#ECEEF2',
      paper: '#F5F6F8',
      slate100: '#D8DCE5'
    },
    support: {
      osprey: '#568EE2',
      navy: '#051C33',
      teal: '#6FB5BA',
      main: '#6FB5BA',
      sand: '#DFD1B6',
      lilac: '#A59FD0'
    },
    lines: {
      grey1: 'rgba(5, 28, 51, 0.23)',
      slate300: '#B1B9CA'
    },
    greyShades: {
      grey1: 'rgba(61, 81, 123, 0.08)',
      grey2: 'rgba(61, 81, 123, 0.16)',
      grey3: 'rgba(61, 81, 123, 0.56)'
    },
    text: {
      primary: 'rgba(5, 28, 51, 0.87)',
      secondary: 'rgba(5, 28, 51, 0.66)',
      slate200: '#C5CBD7'
    },
    action: {
      active: '#3D517B8F'
    }
  },
  shape: {
    borderRadius: 4
  },
  spacing: 4,
  typography: {
    htmlFontSize: 14,
    fontFamily: 'Mulish',
    lineHeight: '1.25rem', // 20px
    marginBottom: '0.375rem', // 6px
    ...sharedTypographyStyles,
    h1: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: '2.25rem', // 36px
      lineHeight: '2.75rem', // 44px
      marginBottom: '2.125rem', // 34px
      letterSpacing: '-0.15px',
      ...sharedTypographyStyles
    },
    h2: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: '1.875rem', // 30px
      lineHeight: '2.25rem', // 36px
      marginBottom: '1.25rem', // 20px
      letterSpacing: '-0.15px',
      ...sharedTypographyStyles
    },
    h3: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: '1.5rem', // 24px
      lineHeight: '2rem', // 32px
      marginBottom: '1.0625rem', // 17px
      letterSpacing: '-0.15px',
      ...sharedTypographyStyles
    },
    h4: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: '1.25rem', // 20px
      lineHeight: '1.75rem', // 28px
      marginBottom: '0.75rem', // 12px
      letterSpacing: '-0.15px',
      ...sharedTypographyStyles
    },
    h5: {
      fontFamily: 'Poppins',
      fontWeight: 500,
      fontSize: '1rem', // 16px
      lineHeight: '1.5rem', // 24px
      marginBottom: '0.5rem', // 8px
      ...sharedTypographyStyles
    },
    h6: {
      fontFamily: 'Poppins',
      fontWeight: 500,
      fontSize: '0.875rem', // 14px
      lineHeight: '1.25rem', // 20px
      marginBottom: '0.4375rem', // 7px
      ...sharedTypographyStyles
    },
    body1: {
      fontFamily: 'Mulish',
      fontWeight: 400,
      fontSize: '0.875rem', // 14px
      lineHeight: '1.25rem', // 20px
      marginBottom: '0.375rem', // 6px
      ...sharedTypographyStyles
    },
    body2: {
      fontFamily: 'Mulish',
      fontWeight: 400,
      fontSize: '0.75rem', // 12px
      lineHeight: '1rem', // 16px
      marginBottom: '0.3125rem', // 5px
      ...sharedTypographyStyles
    },
    subtitle1: {
      fontFamily: 'Poppins',
      fontWeight: 400,
      fontSize: '0.875rem', // 14px
      lineHeight: '1.25rem', // 20px
      marginBottom: '0.375rem', // 6px=
      letterSpacing: '-0.15px',
      ...sharedTypographyStyles
    },
    subtitle2: {
      fontFamily: 'Poppins',
      fontWeight: 500,
      fontSize: '0.75rem', // 12px
      lineHeight: '1rem', // 16px
      marginBottom: '0.3125rem', // 5px
      letterSpacing: '0.1px',
      ...sharedTypographyStyles
    },
    overline: {
      fontFamily: 'Mulish',
      fontWeight: 500,
      fontSize: '0.75rem', // 12px
      lineHeight: '2rem', // 32px
      marginBottom: '0.25rem', // 4px
      textTransform: 'uppercase',
      letterSpacing: '1px',
      ...sharedTypographyStyles
    },
    caption: {
      fontFamily: 'Mulish',
      fontWeight: 500,
      fontSize: '0.6875rem', // 11px
      lineHeight: '1.125rem', // 18px
      marginBottom: '0.25rem', // 4px
      letterSpacing: '0.1px',
      ...sharedTypographyStyles
    }
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: ({ ownerState }) => {
          return {
            ...(ownerState.className.includes('MuiTab') &&
              ownerState.className.includes('Mui-selected') && {
                color: '#5073C3 !important'
              }),
            ...(ownerState.className.includes('MuiRadio') &&
              ownerState.className.includes('Mui-checked') && {
                color: '#5073C3 !important'
              }),
            ...(ownerState.className.includes('MuiCheckbox') &&
              ownerState.className.includes('Mui-checked') && {
                color: '#5073C3 !important'
              })
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #051C331F'
        },
        flexContainer: {
          justifyContent: 'flex-start'
        },
        indicator: {
          backgroundColor: '#5073C3'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          padding: '9px 16px',
          color: 'rgba(5, 28, 51, 0.60)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          boxShadow: 'none',
          ':hover': { boxShadow: 'none' },
          ':focus': { boxShadow: 'none' }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => {
          return {
            textTransform: ownerState.shrink ? 'uppercase' : 'none',
            fontSize: ownerState.shrink ? '1rem' : '0.875rem',
            color: 'rgba(5, 28, 51, 0.60)'
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          marginBottom: 0,
          cursor: 'pointer'
        },
        asterisk: {
          color: '#C33241'
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        label: {
          marginBottom: 0,
          cursor: 'pointer'
        },
        asterisk: {
          color: '#C33241'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          legend: {
            fontSize: '0.75rem',
            textTransform: 'uppercase'
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgb(242, 242, 242)',
            color: 'rgb(128, 128, 128)',
            pointerEvents: 'none',
            fieldset: {
              borderColor: 'rgb(230, 230, 230) !important'
            }
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          legend: {
            fontSize: '0.75rem',
            textTransform: 'uppercase'
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgb(242, 242, 242) !important',
            color: 'rgb(128, 128, 128)',
            pointerEvents: 'none',
            fieldset: {
              borderColor: 'rgb(230, 230, 230) !important'
            }
          },
          '&.Mui-readOnly': {
            backgroundColor: 'rgb(242, 242, 242) !important',
            cursor: 'not-allowed !important',
            userSelect: 'none'
          }
        },
        input: {
          '&.Mui-disabled': {
            backgroundColor: 'rgb(242, 242, 242) !important',
            color: 'rgb(128, 128, 128)',
            pointerEvents: 'none',
            fieldset: {
              borderColor: 'rgb(230, 230, 230) !important'
            }
          },
          '&.Mui-readOnly': {
            backgroundColor: 'rgb(242, 242, 242) !important',
            cursor: 'not-allowed !important',
            userSelect: 'none'
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          color: 'white',
          backgroundColor: '#051C33'
        },
        popper: {
          zIndex: 10000
        },
        arrow: {
          color: '#051C33'
        }
      }
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          zIndex: '10000 !important'
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: 'white',
          fontSize: '0.875rem',
          fontWeight: 600
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '&.MuiAlert-colorInfo': {
            backgroundColor: 'rgba(165, 159, 208, 0.25)',
            'svg path': { fill: '#908cb1' }
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          padding: '8px',
          backgroundColor: 'white',
          minWidth: '90px !important',
          maxHeight: '430px',
          ul: { padding: 0, li: { margin: '1px', borderRadius: '4px', cursor: 'pointer', textTransform: 'none' } }
        }
      }
    }
  }
})

export default themeConfig
